import React, { useState, useEffect } from 'react';
import { logError } from '../../../utils/errorLogger';
import { useParams, useNavigate } from 'react-router-dom';
import { getSupabase } from '../../../supabaseClient';
import toast from 'react-hot-toast';
import PropTypes from 'prop-types';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const parseRequiredParts = (parts) => {
  if (typeof parts === 'string') {
    try {
      return JSON.parse(parts);
    } catch (error) {
      logError('Erreur de parsing des pièces requises', error);
      return [{ case: '', parts: [] }];
    }
  }
  return Array.isArray(parts) ? parts : [{ case: '', parts: [] }];
};

const ConsularActForm = ({ 
  onSuccess, 
  isModal = false,
  initialData = null
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const editId = initialData?.id || id;
  const isEditMode = !!editId;
  
  // Fonction utilitaire pour transformer les null en '' dans shipping_fees
  const normalizeShippingFees = (fees) => {
    const zones = ['France', 'Europe', 'DOM-TOM'];
    if (!Array.isArray(fees)) {
      return zones.map(zone => ({ [zone]: '' }));
    }
    return zones.map(zone => {
      const found = fees.find(fee => Object.keys(fee)[0] === zone);
      return { [zone]: found && found[zone] != null ? found[zone] : '' };
    });
  };

  // Parse shipping_fees si c'est une string JSON
  let parsedShippingFees = initialData?.shipping_fees;
  if (typeof parsedShippingFees === 'string') {
    try {
      parsedShippingFees = JSON.parse(parsedShippingFees);
    } catch (e) {
      parsedShippingFees = undefined;
    }
  }

  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    description: initialData?.description || '',
    category_id: initialData?.category_id || '',
    processing_time: initialData?.processing_time || '',
    processing_fee: initialData?.processing_fee || '',
    shipping_fee: initialData?.shipping_fee || '',
    shipping_fees: normalizeShippingFees(parsedShippingFees),
    form_url: initialData?.form_url || '',
    required_parts: parseRequiredParts(initialData?.required_parts)
  });
  
  // États pour l'édition directe
  const [editingCase, setEditingCase] = useState(null);
  const [editingPart, setEditingPart] = useState(null);
  const [editingCaseText, setEditingCaseText] = useState('');
  const [editingPartText, setEditingPartText] = useState('');

  // Si initialData change dynamiquement (ex: après fetch), on réapplique la normalisation
  React.useEffect(() => {
    let fees = initialData?.shipping_fees;
    if (typeof fees === 'string') {
      try {
        fees = JSON.parse(fees);
      } catch (e) {
        fees = undefined;
      }
    }
    setFormData(prev => ({
      ...prev,
      shipping_fees: normalizeShippingFees(fees)
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);
  
  const [newCase, setNewCase] = useState('');
  const [newRequiredPart, setNewRequiredPart] = useState('');
  const [selectedCaseIndex, setSelectedCaseIndex] = useState(0);

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(isEditMode);
  const [error, setError] = useState('');

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ]
  };

  const formats = [
    'header',
    'bold', 
    'italic', 
    'underline', 
    'strike',
    'list', 
    'bullet',
    'link'
  ];

  const handleDescriptionChange = (value) => {
    setFormData(prev => ({
      ...prev,
      description: value
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleShippingFeesChange = (zone, value) => {
    setFormData(prev => ({
      ...prev,
      shipping_fees: prev.shipping_fees.map(item => 
        item[zone] !== undefined ? { [zone]: value ? parseFloat(value) : null } : item
      )
    }));
  };

  useEffect(() => {
    const fetchCategories = async () => {
      const supabase = getSupabase();
      const { data, error } = await supabase
        .from('categories')
        .select(`
          id, 
          name, 
          description,
          services (id, name)
        `)
        .eq('services.name', 'Actes consulaires')
        .order('name', { ascending: true });

      if (error) {
        toast.error('Erreur lors du chargement des catégories');
        console.error(error);
      } else {
        setCategories(data || []);
      }
    };

    fetchCategories();

    if (isEditMode && !initialData) {
      const fetchConsularActDetails = async () => {
        const supabase = getSupabase();
        const { data, error } = await supabase
          .from('consular_acts')
          .select(`
            id,
            title,
            description,
            category_id,
            processing_time,
            processing_fee,
            shipping_fee,
            shipping_fees,
            form_url,
            required_parts
          `)
          .eq('id', editId)
          .single();

        if (error) {
          toast.error('Erreur lors du chargement de l\'acte consulaire');
          logError('Erreur lors de la récupération des détails de l\'acte consulaire', error);
        } else {
          setFormData({
            ...data,
            shipping_fees: normalizeShippingFees(data.shipping_fees),
            required_parts: parseRequiredParts(data.required_parts)
          });
        }
      };

      fetchConsularActDetails();
    }
    
    setLoading(false);
  }, [editId, isEditMode, initialData]);

  const handleAddCase = () => {
    if (!newCase.trim()) {
      setError('Veuillez saisir un libellé de cas');
      return;
    }

    setFormData(prev => ({
      ...prev,
      required_parts: [
        ...prev.required_parts, 
        { case: newCase, parts: [] }
      ]
    }));

    setNewCase('');
    setSelectedCaseIndex(formData.required_parts.length);
  };

  const handleAddRequiredPart = () => {
    if (!newRequiredPart.trim()) {
      setError('Veuillez saisir une pièce requise');
      return;
    }

    const updatedRequiredParts = [...formData.required_parts];
    updatedRequiredParts[selectedCaseIndex].parts.push(newRequiredPart);

    setFormData(prev => ({
      ...prev,
      required_parts: updatedRequiredParts
    }));

    setNewRequiredPart('');
  };

  const handleRemoveRequiredPart = (caseIndex, partIndex) => {
    const updatedRequiredParts = [...formData.required_parts];
    updatedRequiredParts[caseIndex].parts.splice(partIndex, 1);

    setFormData(prev => ({
      ...prev,
      required_parts: updatedRequiredParts
    }));
  };

  const handleRemoveCase = (caseIndex) => {
    setFormData(prev => ({
      ...prev,
      required_parts: prev.required_parts.filter((_, index) => index !== caseIndex)
    }));
    
    // Réinitialiser l'index sélectionné si nécessaire
    if (selectedCaseIndex >= caseIndex && selectedCaseIndex > 0) {
      setSelectedCaseIndex(selectedCaseIndex - 1);
    }
  };
  
  // Fonction pour commencer l'édition d'un cas
  const startEditingCase = (caseIndex) => {
    setEditingCase(caseIndex);
    setEditingCaseText(formData.required_parts[caseIndex].case);
  };
  
  // Fonction pour commencer l'édition d'une pièce requise
  const startEditingPart = (caseIndex, partIndex) => {
    setEditingPart({ caseIndex, partIndex });
    setEditingPartText(formData.required_parts[caseIndex].parts[partIndex]);
  };
  
  // Fonction pour sauvegarder l'édition d'un cas
  const saveEditingCase = () => {
    if (editingCase !== null) {
      setFormData(prev => ({
        ...prev,
        required_parts: prev.required_parts.map((item, index) => 
          index === editingCase 
            ? { ...item, case: editingCaseText } 
            : item
        )
      }));
      setEditingCase(null);
    }
  };
  
  // Fonction pour sauvegarder l'édition d'une pièce requise
  const saveEditingPart = () => {
    if (editingPart !== null) {
      const { caseIndex, partIndex } = editingPart;
      setFormData(prev => ({
        ...prev,
        required_parts: prev.required_parts.map((item, idx) => 
          idx === caseIndex 
            ? { 
                ...item, 
                parts: item.parts.map((part, pIdx) => 
                  pIdx === partIndex ? editingPartText : part
                ) 
              } 
            : item
        )
      }));
      setEditingPart(null);
    }
  };
  
  // Gestionnaire d'événements pour la touche Entrée
  const handleKeyDown = (e, saveFunction) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveFunction();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setEditingCase(null);
      setEditingPart(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation des champs obligatoires
    if (!formData.title || !formData.category_id) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      const supabase = getSupabase();

      // Vérification des doublons
      const { data: existingActs, error: checkError } = await supabase
        .from('consular_acts')
        .select('id, title')
        .eq('title', formData.title)
        .eq('category_id', formData.category_id);

      if (checkError) {
        logError('Erreur lors de la vérification des doublons', checkError);
        toast.error('Erreur lors de la vérification des doublons');
        return;
      }

      // Si un acte avec le même titre et la même catégorie existe déjà
      if (existingActs.length > 0 && !isEditMode) {
        toast.error('Un acte consulaire avec ce titre existe déjà dans cette catégorie');
        return;
      }

      const preparedData = {
        title: formData.title,
        description: formData.description,
        category_id: formData.category_id,
        processing_time: formData.processing_time || null,
        processing_fee: formData.processing_fee ? parseFloat(formData.processing_fee) : null,
        shipping_fee: formData.shipping_fee ? parseFloat(formData.shipping_fee) : null,
        shipping_fees: formData.shipping_fees,
        form_url: formData.form_url || null,
        required_parts: JSON.stringify(formData.required_parts)
      };

      let result;
      if (isEditMode) {
        console.log('Mode édition, ID:', editId);
        result = await supabase
          .from('consular_acts')
          .update(preparedData)
          .eq('id', editId)
          .select(); 
      } else {
        console.log('Mode création');
        result = await supabase
          .from('consular_acts')
          .insert(preparedData)
          .select();
      }

      if (result.error) {
        logError('Erreur lors de la sauvegarde', result.error);
        toast.error(`Erreur : ${result.error.message}`);
        return;
      }

      toast.success(isEditMode ? 'Acte mis à jour' : 'Acte créé');
      
      if (onSuccess) {
        onSuccess(result.data[0]);
      }

      if (!isModal) {
        navigate('/admin/consular-acts');
      }

    } catch (error) {
      logError('Erreur inattendue', error);
      toast.error('Une erreur est survenue');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-secondary"></div>
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="bg-white rounded-lg">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="title">
              Titre
            </label>
            <input
              id="title"
              name="title"
              type="text"
              required
              className="tracking-input w-full form-input"
              value={formData.title}
              onChange={handleChange}
            />
          </div>
          
          <div className="md:col-span-3">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="description">
              Description
            </label>
            <ReactQuill 
              theme="snow" 
              modules={modules} 
              formats={formats} 
              value={formData.description} 
              onChange={handleDescriptionChange}
            />
          </div>
          
          <div className="col-span-1">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="category_id">
              Catégorie
            </label>
            <select
              id="category_id"
              name="category_id"
              required
              className="tracking-input w-full form-input"
              value={formData.category_id}
              onChange={handleChange}
            >
              <option value="">Sélectionner une catégorie</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </select>
          </div>
          
          <div className="col-span-1">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="processing_time">
              Délai de traitement
            </label>
            <input
              id="processing_time"
              name="processing_time"
              type="text"
              className="tracking-input w-full form-input"
              placeholder="ex: 15 jours"
              value={formData.processing_time}
              onChange={handleChange}
            />
          </div>
          
          <div className="col-span-1">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="processing_fee">
              Droit de chancellerie (€)
            </label>
            <input
              id="processing_fee"
              name="processing_fee"
              type="number"
              step="0.01"
              min="0"
              className="tracking-input w-full form-input"
              value={formData.processing_fee}
              onChange={handleChange}
            />
          </div>
          
          {/*<div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="shipping_fee">
              Frais d&apos;expédition (€)
            </label>
            <input
              id="shipping_fee"
              name="shipping_fee"
              type="number"
              step="0.01"
              min="0"
              required
              className="tracking-input w-full form-input"
              value={formData.shipping_fee}
              onChange={handleChange}
            />
          </div>*/}
          
          <div className="col-span-3">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="shipping_fees">
              Frais d&apos;expédition par zone (€)
            </label>
            {console.log('DEBUG shipping_fees:', formData.shipping_fees)}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Array.isArray(formData.shipping_fees) && formData.shipping_fees.map((fee, index) => {
                const zone = Object.keys(fee)[0];
                return (
                  <div key={index}>
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor={`shipping_fees_${zone}`}>
                      {zone}
                    </label>
                    <input
                      id={`shipping_fees_${zone}`}
                      name={`shipping_fees_${zone}`}
                      type="number"
                      step="0.01"
                      min="0"
                      className="tracking-input w-full form-input"
                      value={fee[zone] ?? ''}
                      onChange={(e) => handleShippingFeesChange(zone, e.target.value)}
                    />
                  </div>
                );
              })}
            </div>
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="form_url">
              URL du formulaire
            </label>
            <input
              id="form_url"
              name="form_url"
              type="url"
              className="tracking-input w-full form-input"
              placeholder="https://example.com/form"
              value={formData.form_url}
              onChange={handleChange}
            />
          </div>
          
          <div className="md:col-span-3">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Pièces requises par cas
            </label>
            
            <div className="flex mb-4">
              <input
                type="text"
                className="tracking-input w-full form-input rounded-r-none"
                placeholder="Ajouter un cas"
                value={newCase}
                onChange={(e) => setNewCase(e.target.value)}
              />
              <button
                type="button"
                onClick={handleAddCase}
                className="bg-secondary text-white px-4 py-2 rounded-r hover:bg-secondary-hover"
              >
                Ajouter
              </button>
            </div>

            {formData.required_parts.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <h3 className="font-semibold mb-2">Cas</h3>
                  <ul className="space-y-2">
                    {formData.required_parts.map((requiredPart, index) => (
                      <li 
                        key={index} 
                        className={`flex justify-between items-center p-2 rounded ${selectedCaseIndex === index ? 'bg-gray-200' : 'hover:bg-gray-100'}`}
                      >
                        {editingCase === index ? (
                          <div className="flex-grow flex">
                            <input
                              type="text"
                              className="tracking-input w-full form-input py-1 px-2"
                              value={editingCaseText}
                              onChange={(e) => setEditingCaseText(e.target.value)}
                              onKeyDown={(e) => handleKeyDown(e, saveEditingCase)}
                              onBlur={saveEditingCase}
                              autoFocus
                            />
                            <button
                              type="button"
                              onClick={saveEditingCase}
                              className="bg-green-500 text-white px-2 py-1 ml-2 rounded hover:bg-green-600"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </button>
                          </div>
                        ) : (
                          <span 
                            onClick={() => setSelectedCaseIndex(index)}
                            onDoubleClick={() => startEditingCase(index)}
                            className="flex-grow cursor-pointer"
                          >
                            {requiredPart.case || 'Cas sans nom'}
                          </span>
                        )}
                        {formData.required_parts.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveCase(index)}
                            className="text-red-600 hover:text-red-800 ml-2"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="md:col-span-2">
                  <h3 className="font-semibold mb-2">Pièces requises pour {formData.required_parts[selectedCaseIndex].case || 'ce cas'}</h3>
                  
                  <div className="flex mb-2">
                    <input
                      type="text"
                      className="tracking-input w-full rounded-r-none form-input"
                      placeholder="Ajouter une pièce requise"
                      value={newRequiredPart}
                      onChange={(e) => setNewRequiredPart(e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={handleAddRequiredPart}
                      className="bg-secondary text-white px-4 py-2 rounded-r hover:bg-secondary-hover"
                    >
                      Ajouter
                    </button>
                  </div>

                  <ul className="space-y-2">
                    {formData.required_parts[selectedCaseIndex].parts.map((part, index) => (
                      <li key={index} className="flex justify-between items-center bg-gray-100 p-2 rounded">
                        {editingPart && editingPart.caseIndex === selectedCaseIndex && editingPart.partIndex === index ? (
                          <div className="flex-grow flex">
                            <input
                              type="text"
                              className="tracking-input w-full form-input py-1 px-2"
                              value={editingPartText}
                              onChange={(e) => setEditingPartText(e.target.value)}
                              onKeyDown={(e) => handleKeyDown(e, saveEditingPart)}
                              onBlur={saveEditingPart}
                              autoFocus
                            />
                            <button
                              type="button"
                              onClick={saveEditingPart}
                              className="bg-green-500 text-white px-2 py-1 ml-2 rounded hover:bg-green-600"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </button>
                          </div>
                        ) : (
                          <span 
                            className="flex-grow cursor-pointer"
                            onDoubleClick={() => startEditingPart(selectedCaseIndex, index)}
                          >
                            {part}
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => handleRemoveRequiredPart(selectedCaseIndex, index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="flex justify-end mt-6 space-x-4">
          <button
            type="button"
            onClick={() => navigate('/admin/consular-acts')}
            className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
          >
            Annuler
          </button>
          <button
            type="submit"
            className="btn-primary"
          >
            {isEditMode ? 'Mettre à jour' : 'Créer'}
          </button>
        </div>
      </form>
    </div>
  );
};

ConsularActForm.propTypes = {
  onSuccess: PropTypes.func,
  isModal: PropTypes.bool,
  initialData: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    description: PropTypes.string,
    category_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    processing_time: PropTypes.string,
    processing_fee: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    shipping_fee: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    shipping_fees: PropTypes.arrayOf(PropTypes.object),
    form_url: PropTypes.string,
    required_parts: PropTypes.arrayOf(PropTypes.shape({
      case: PropTypes.string,
      parts: PropTypes.arrayOf(PropTypes.string)
    }))
  })
};

export default ConsularActForm;
